import type { TpaInnerRouteObject } from '@wix/document-services-types';
import * as Actions from './tpaDynamicPagesActionTypes';

export type IgnoreSubPagesFeature = keyof SubPagesToIgnore;

export interface TpaInnerRouteData extends TpaInnerRouteObject {
  subPage: string;
}

export interface TpaSubPagesInnerRoutes {
  [subPage: string]: TpaInnerRouteData[];
}

export interface InnerRoutesCounts {
  [appDefinitionId: string]: {
    [pageId: string]: {
      [subPage: string]: number | undefined;
    };
  };
}

export interface CachedInnerRoutes {
  [appDefinitionId: string]: {
    pages: {
      [pageId: string]: {
        subPages: TpaSubPagesInnerRoutes;
        subPagesToIgnore?: SubPagesToIgnore;
      };
    };
  };
}

export interface SubPageData {
  key: string;
  hideFromFloatingNavBar?: boolean;
  hideFromLinkPanel?: boolean;
}

export interface SubPagesToIgnore {
  linkPanel?: string[];
  dynamicPagesNavBar?: string[];
}

export interface CurrentTpaInnerRoutesInfo {
  appDefinitionId: string;
  pageId: string;
  subPage: string;
  innerRouteTitle: string;
  path: string;
}

export interface TpaDynamicPagesState {
  cachedInnerRoutes: CachedInnerRoutes;
  selectedInnerRoute: TpaInnerRouteData;
  inInnerRoutesFetchProcess: boolean;
  currentPageSessionId: string;
  currentTpaInfo: CurrentTpaInnerRoutesInfo;
  innerRoutesCounts: InnerRoutesCounts;
}

type ActionType = (typeof Actions)[keyof typeof Actions];

export interface TpaDynamicPageStateAction {
  type: ActionType;
  [param: string]: any;
}

export const tpaDynamicPagesInitialState: TpaDynamicPagesState = {
  selectedInnerRoute: null,
  inInnerRoutesFetchProcess: false,
  currentPageSessionId: '',
  currentTpaInfo: null,
  innerRoutesCounts: {},
  cachedInnerRoutes: {},
};

const setCachedInnerRoutes = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => {
  const { appDefinitionId, pageId, subPage, innerRoutes } = action;
  const cachedInnerRoutes = tpaDynamicPages.cachedInnerRoutes;
  const innerRoutesCounts = tpaDynamicPages.innerRoutesCounts;

  return {
    ...tpaDynamicPages,
    cachedInnerRoutes: {
      ...cachedInnerRoutes,
      [appDefinitionId]: {
        ...(cachedInnerRoutes[appDefinitionId] ?? {}),
        pages: {
          ...(cachedInnerRoutes[appDefinitionId]?.pages ?? {}),
          [pageId]: {
            ...(cachedInnerRoutes[appDefinitionId]?.pages?.[pageId] ?? {}),
            subPages: {
              ...(cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
                ?.subPages ?? {}),
              [subPage]: innerRoutes,
            },
          },
        },
      },
    },
    innerRoutesCounts: {
      ...innerRoutesCounts,
      [appDefinitionId]: {
        ...(innerRoutesCounts[appDefinitionId] ?? {}),
        [pageId]: {
          ...(innerRoutesCounts[appDefinitionId]?.[pageId] ?? {}),
          [subPage]: innerRoutes?.length,
        },
      },
    },
  };
};

const setFeaturesSubPagesToIgnore = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => {
  const { appDefinitionId, pageId, subPagesToIgnore } = action;
  const cachedInnerRoutes = tpaDynamicPages.cachedInnerRoutes;

  return {
    ...tpaDynamicPages,
    cachedInnerRoutes: {
      ...cachedInnerRoutes,
      [appDefinitionId]: {
        ...(cachedInnerRoutes[appDefinitionId] ?? {}),
        pages: {
          ...(cachedInnerRoutes[appDefinitionId]?.pages ?? {}),
          [pageId]: {
            ...(cachedInnerRoutes[appDefinitionId]?.pages?.[pageId] ?? {}),
            subPagesToIgnore,
          },
        },
      },
    },
  };
};

const setCurrentInnerRoute = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => {
  const { appDefinitionId, pageId, selectedInnerRoute } = action;

  if (!selectedInnerRoute) {
    return {
      ...tpaDynamicPages,
      currentTpaInfo: {
        appDefinitionId,
        pageId,
        subPage: undefined,
        innerRouteTitle: undefined,
        path: undefined,
      },
      selectedInnerRoute: null,
    };
  }

  return {
    ...tpaDynamicPages,
    currentTpaInfo: {
      appDefinitionId,
      pageId,
      subPage: selectedInnerRoute.subPage,
      innerRouteTitle: selectedInnerRoute.name,
      path: selectedInnerRoute.path,
    },
    selectedInnerRoute,
  };
};

const setIsInFetchProcess = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => ({
  ...tpaDynamicPages,
  inInnerRoutesFetchProcess: action.inInnerRoutesFetchProcess,
});

const setCurrentPageSessionId = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => ({
  ...tpaDynamicPages,
  currentPageSessionId: action.tpaPageSessionId,
});

const clearInnerRoutesFromCache = (
  tpaDynamicPages: TpaDynamicPagesState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => {
  const { appDefinitionId } = action;
  if (!tpaDynamicPages.cachedInnerRoutes[appDefinitionId]) {
    return tpaDynamicPages;
  }

  return {
    ...tpaDynamicPages,
    cachedInnerRoutes: {
      ...tpaDynamicPages.cachedInnerRoutes,
      [appDefinitionId]: undefined,
    },
    innerRoutesCounts: {
      ...tpaDynamicPages.innerRoutesCounts,
      [appDefinitionId]: undefined,
    },
    currentTpaInfo: null,
  };
};

const reducer = (
  tpaDynamicPages: TpaDynamicPagesState = tpaDynamicPagesInitialState,
  action: TpaDynamicPageStateAction,
): TpaDynamicPagesState => {
  switch (action.type) {
    case Actions.SET_TPA_CACHED_INNER_ROUTES:
      return setCachedInnerRoutes(tpaDynamicPages, action);

    case Actions.SET_FEATURES_SUB_PAGES_TO_IGNORE:
      return setFeaturesSubPagesToIgnore(tpaDynamicPages, action);

    case Actions.SET_CURRENT_TPA_INNER_ROUTE:
      return setCurrentInnerRoute(tpaDynamicPages, action);

    case Actions.SET_TPA_INNER_ROUTES_FETCH_PROCESS:
      return setIsInFetchProcess(tpaDynamicPages, action);

    case Actions.SET_TPA_PAGE_SESSION_ID:
      return setCurrentPageSessionId(tpaDynamicPages, action);

    case Actions.RESET_TPA_CACHED_INNER_ROUTES:
      return clearInnerRoutesFromCache(tpaDynamicPages, action);

    default:
      return tpaDynamicPages;
  }
};

export default reducer;
